/* eslint-disable max-len */
import React from 'react';
import styled from 'styled-components';
import {useWindowSize} from '../../../hooks/useWindowSize';
import {ReactComponent as Background} from '../../../assets/cnito/ill_games.svg';
import {ReactComponent as BackgroundTablet} from '../../../assets/cnito/ill_games_tablet.svg';
import {ReactComponent as BackgroundWeb} from '../../../assets/cnito/ill_games_web.svg';

import {ReactComponent as BackgroundXmas} from '../../../assets/cnito/ill_games_xmas.svg';
import {ReactComponent as BackgroundTabletXmas} from '../../../assets/cnito/ill_games_tablet_xmas.svg';
import {ReactComponent as BackgroundWebXmas} from '../../../assets/cnito/ill_games_web_xmas.svg';
import AnimatedCharacter from '../../AnimatedCharacter';
import {
    mobileMediaQuery,
    notWebMediaQuery,
    tabletMediaQuery,
} from '../../../styles';
import {Player} from '@lottiefiles/react-lottie-player';
import {useSelector} from 'react-redux';
import {selectHolidayTheme} from '../../../store/reducers';

const GamesHeader = () => {
    const {width} = useWindowSize();
    const holidayTheme = useSelector(selectHolidayTheme);

    if (!width) {
        return null;
    }

    const maxWidth = Math.min(width, 1440);
    let height = (maxWidth * 100) / 768;

    return (
        <>
            {width > 1024 && (
                <World height={height}>
                    <svg width={width} height={width / 3}>
                        <circle
                            cx={width / 2}
                            cy={maxWidth * 2.4 + 50 + (maxWidth - 1024) * 0.051}
                            r={maxWidth * 2.4}
                            fill="#F6D703"
                        />
                    </svg>
                </World>
            )}
            <Container vw={maxWidth / 100}>
                {width <= 600 &&
                    (holidayTheme === 'xmas' ? (
                        <StyledBackgroundXmas
                            width={maxWidth}
                            height={height}
                        />
                    ) : (
                        <StyledBackground width={maxWidth} height={height} />
                    ))}
                {width > 600 &&
                    width <= 1024 &&
                    (holidayTheme === 'xmas' ? (
                        <StyledBackgroundTabletXmas
                            width={maxWidth}
                            height={height}
                        />
                    ) : (
                        <StyledBackgroundTablet
                            width={maxWidth}
                            height={height}
                        />
                    ))}
                {width > 1024 &&
                    (holidayTheme === 'xmas' ? (
                        <StyledBackgroundWebXmas
                            width={maxWidth}
                            height={height}
                        />
                    ) : (
                        <StyledBackgroundWeb width={maxWidth} height={height} />
                    ))}
                <PositionedAvatar
                    vw={maxWidth / 100}
                    idleData={require('../../../assets/animations/game_wedge_idle.json')}
                    animatedData={[
                        require('../../../assets/animations/game_wedge_laugh.json'),
                        require('../../../assets/animations/game_wedge_jump.json'),
                        require('../../../assets/animations/game_wedge_spin.json'),
                    ]}
                    background={require('../../../assets/animations/anim_confetti.json')}
                    hitSlop={{top: 45, left: 20, right: 10}}
                />
                {holidayTheme !== 'xmas' && (
                    <LeftTree
                        autoplay
                        loop
                        src={require('../../../assets/animations/games_palmtree_left')}
                        showControls={false}
                        maxWidth={maxWidth}
                    />
                )}
                {holidayTheme !== 'xmas' && (
                    <RightTree
                        autoplay
                        loop
                        src={require('../../../assets/animations/games_palmtree_right')}
                        showControls={false}
                        maxWidth={maxWidth}
                    />
                )}
            </Container>
        </>
    );
};

const Container = styled.div`
    position: relative;
    width: ${props => 100 * props.vw}px;
    height: ${props => (8000 * props.vw) / 768}px;
    margin-top: calc(100vw / 15);
    display: flex;
    margin: auto;
    left: 0;
    right: 0;
    top: ${props => 5 * props.vw}px;
`;

const StyledBackground = styled(Background)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

const StyledBackgroundXmas = styled(BackgroundXmas)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

const StyledBackgroundTablet = styled(BackgroundTablet)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

const StyledBackgroundTabletXmas = styled(BackgroundTabletXmas)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

const StyledBackgroundWeb = styled(BackgroundWeb)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

const StyledBackgroundWebXmas = styled(BackgroundWebXmas)`
    position: absolute;
    width: ${props => props.width}px;
    height: ${props => props.height * 3}px;
    left: 0px;
    top: ${props => -0.5 * props.height}px;
`;

export default GamesHeader;

const PositionedAvatar = styled(AnimatedCharacter)`
    position: absolute;
    left: 0px;
    right: 0px;
    margin: auto;
    width: 20vw;
    height: 20vw;
    top: -13.5vw;
    width: ${props => 16 * props.vw}px;
    height: ${props => 16 * props.vw}px;
    top: ${props => -11 * props.vw}px;

    ${notWebMediaQuery} {
        width: ${props => 12 * props.vw}px;
        height: ${props => 15 * props.vw}px;
        top: ${props => -10 * props.vw}px;
    }
`;

const World = styled.div`
    position: absolute;
    width: 100%;
`;

const LeftTree = styled(Player)`
    position: absolute;
    left: ${props => props.maxWidth / 7}px;
    top: ${props => -props.maxWidth / 25}px;
    height: ${props => props.maxWidth / 15}px;
    width: ${props => props.maxWidth / 15}px;

    ${tabletMediaQuery} {
        top: ${props => -props.maxWidth / 28}px;
    }

    ${mobileMediaQuery} {
        left: ${props => props.maxWidth / 10}px;
        top: ${props => -props.maxWidth / 20}px;
    }
`;

const RightTree = styled(Player)`
    position: absolute;
    right: ${props => props.maxWidth / 12}px;
    top: ${props => -props.maxWidth / 30}px;
    height: ${props => props.maxWidth / 15}px;
    width: ${props => props.maxWidth / 15}px;

    ${tabletMediaQuery} {
        top: ${props => -props.maxWidth / 34}px;
    }

    ${mobileMediaQuery} {
        right: ${props => props.maxWidth / 13}px;
        top: ${props => -props.maxWidth / 20}px;
    }
`;
